/**
 * ParticleManager — singleton por escena que orquesta todos los emitters.
 *
 * Gestiona DOS motores transparentemente:
 *   - v2 nativo (Phaser.GameObjects.Particles.ParticleEmitter via adapter).
 *     Usado por default. Trae batching, pool optimizado y mejor performance.
 *   - v1 sprite-based (ParticleEmitter custom) como fallback automático para
 *     defs con features que el adapter v2 no soporta: orbit, depthMode='orbit',
 *     sub-emitters, trail, noise.
 *
 * Ambos motores se exponen vía el mismo `EmitterHandle` opaco. El caller
 * (GameScene) no distingue cuál se está usando.
 *
 * Responsabilidades:
 *  - Anchor resolution per frame (entity remoto, projectile, item_ground).
 *  - Frustum culling: pausa emisión si fuera del viewport + margen.
 *  - Budget global: cap de emitters activos; killea por prioridad ascendente.
 *  - Cleanup masivo por ownerId.
 */

import type Phaser from 'phaser';
import { getParticleEffect } from '@shared/data/particleEffects';
import type { ParticleEffectDefV2 } from '@shared/types/particles';
import {
  AnchorResolver,
  type Anchor,
  type AnchorResolverContext,
  type EntityAnchorSlot,
  type EntityRenderable,
} from './AnchorResolver';
import { phaserNativeEmitterAdapter } from './phaserNativeEmitterAdapter';
import { ParticleEmitter, PARTICLE_SHEET } from './ParticleSystem';
import { v2ToV1 } from '../scenes/particleEditor/v1v2Convert';

// ────────────────────────────────────────────────────────────────────────────
// Tipos públicos
// ────────────────────────────────────────────────────────────────────────────

export type Priority = 'low' | 'normal' | 'high';

export interface SpawnOptions {
  /** Para cleanup masivo (ej. stopAllByOwner al desconectar entidad). */
  ownerId?: string;
  /** Si excede el budget global, killea handles 'low' antes que 'normal'. */
  priority?: Priority;
  loopOverride?: boolean;
  durationOverride?: number;
  /** Desactiva LOD para este handle (FX críticos). */
  disableLOD?: boolean;
}

/** Handle opaco — para stop/setPriority. */
export interface EmitterHandle {
  readonly id: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Estado interno
// ────────────────────────────────────────────────────────────────────────────

interface ActiveEmitterV2 {
  engine: 'v2';
  emitter: Phaser.GameObjects.Particles.ParticleEmitter;
}

interface ActiveEmitterV1 {
  engine: 'v1';
  emitter: ParticleEmitter;
}

type ActiveEmitterBackend = ActiveEmitterV2 | ActiveEmitterV1;

interface ActiveEmitter {
  id: number;
  fxId: string;
  def: ParticleEffectDefV2;
  anchor: Anchor;
  ownerId: string | undefined;
  priority: Priority;
  disableLOD: boolean;
  startedAt: number;
  backend: ActiveEmitterBackend;
  /** false cuando hay que limpiar en el próximo updateAll. */
  alive: boolean;
  /** BitmapMask creada por clipToAnchorSprite; se destruye junto al emitter. */
  bitmapMask?: Phaser.Display.Masks.BitmapMask;
  /** Última (x, y) del anchor resuelto. Usado para el delta de entityFollow. */
  lastAnchorX?: number;
  lastAnchorY?: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Mapea un slot de entity-anchor al sprite renderable correspondiente para
 * usarse como fuente de BitmapMask. Devuelve null para slots no-equipo o
 * cuando la pieza no está equipada (sprite ausente en el renderable).
 */
function pickAnchorSpriteForSlot(
  entity: EntityRenderable,
  slot: EntityAnchorSlot
): Phaser.GameObjects.Sprite | null {
  switch (slot) {
    case 'weapon_tip':
    case 'weapon_hilt':
      return entity.weapon ?? null;
    case 'shield_front':
      return entity.shield ?? null;
    case 'helm_top':
      return entity.helm ?? entity.head ?? null;
    default:
      // feet/center/head/above no son slots de equipo "recortables".
      return null;
  }
}

/**
 * Mueve las partículas vivas del backend dado por (dx, dy) en world space.
 * Usado por el `entityFollow` para arrastrar parcialmente las partículas
 * con el anchor cuando éste se desplaza.
 */
function shiftAliveParticles(
  backend: ActiveEmitterBackend,
  dx: number,
  dy: number
): void {
  if (backend.engine === 'v2') {
    // API pública Phaser 3.60+: itera todas las partículas vivas y muta x/y.
    backend.emitter.forEachAlive(
      (p: Phaser.GameObjects.Particles.Particle) => {
        p.x += dx;
        p.y += dy;
      },
      null
    );
  } else {
    backend.emitter.shiftAliveParticles(dx, dy);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Budget global
// ────────────────────────────────────────────────────────────────────────────

const BUDGET_MAX_EMITTERS = 256;

// LOD: distancia en px desde el centro de la cámara → multiplicador de rate.
const LOD_NEAR = 512;
const LOD_FAR = 1024;

// ────────────────────────────────────────────────────────────────────────────
// ParticleManager
// ────────────────────────────────────────────────────────────────────────────

export class ParticleManager {
  private readonly scene: Phaser.Scene;
  private readonly resolver: AnchorResolver;
  private readonly ctx: AnchorResolverContext;
  private readonly emitters = new Map<number, ActiveEmitter>();
  private nextId = 1;

  constructor(scene: Phaser.Scene, ctx: AnchorResolverContext) {
    this.scene = scene;
    this.ctx = ctx;
    this.resolver = new AnchorResolver(ctx);
  }

  // ── API pública ─────────────────────────────────────────────────────────

  /**
   * Spawn de un FX en el anchor dado. Intenta motor v2 nativo; si la def usa
   * features que el adapter no soporta (orbit/sub-emitters/etc), fallback al
   * motor v1 transparentemente. Devuelve null solo si el fxId no existe o el
   * anchor inicial es inválido.
   */
  spawn(fxId: string, anchor: Anchor, opts: SpawnOptions = {}): EmitterHandle | null {
    const baseDef = getParticleEffect(fxId);
    if (!baseDef) {
      console.warn(`[ParticleManager] fxId desconocido: ${fxId}`);
      return null;
    }

    // Para projectiles, convertimos el FX a un burst único de partículas que
    // viajan al target via `moveTo` nativo de Phaser. Cada partícula vive
    // exactamente lo que dura el vuelo y muere al llegar al tile destino.
    const def = anchor.kind === 'projectile'
      ? this.overrideForProjectile(baseDef, anchor.durationMs)
      : baseDef;

    this.enforceBudget(opts.priority ?? 'normal');

    const resolved = this.resolver.resolve(anchor);
    if (!resolved.valid) {
      console.warn(`[ParticleManager] anchor inválido para fx="${fxId}"`);
      return null;
    }

    // Para projectile: calcular moveTo local (target - origin) y pasar al
    // adapter. Phaser hará que cada partícula viaje a ese punto local en
    // su lifespan = flightMs.
    let moveTo: { x: number; y: number } | undefined;
    if (anchor.kind === 'projectile') {
      const fromR = this.resolver.resolve(anchor.from);
      const toR = this.resolver.resolve(anchor.to);
      if (fromR.valid && toR.valid) {
        moveTo = { x: toR.x - fromR.x, y: toR.y - fromR.y };
      }
    }

    // ── 1) Intentar motor v2 nativo ────────────────────────────────────────
    const v2 = phaserNativeEmitterAdapter(this.scene, def, resolved.x, resolved.y, moveTo);
    let backend: ActiveEmitterBackend;
    if (v2) {
      v2.emitter.setDepth(resolved.depth + (def.depthOffset ?? 0));
      backend = { engine: 'v2', emitter: v2.emitter };
      // Si el adapter dejó el emitter en modo burst (frequency=-1), nadie
      // emite por nosotros — disparamos manualmente todas las partículas.
      if (v2.isBurst) {
        const count = def.emission.burstCount ?? def.emission.maxAlive;
        v2.emitter.explode(count, 0, 0);
      }
    } else {
      // ── 2) Fallback motor v1 (sprite pool, soporta orbit + depthMode) ────
      backend = this.createV1Backend(def, resolved.x, resolved.y, resolved.depth);
    }

    // ── 3) Clip por sprite del anchor (BitmapMask) ────────────────────────
    const bitmapMask = this.tryCreateAnchorClipMask(def, anchor);
    if (bitmapMask) {
      if (backend.engine === 'v2') backend.emitter.setMask(bitmapMask);
      else backend.emitter.setMask(bitmapMask);
      console.log(
        `[ParticleManager] clip mask APLICADO a fx="${fxId}" backend=${backend.engine}`
      );
    } else if (def.clipToAnchorSprite) {
      console.log(
        `[ParticleManager] clipToAnchorSprite=true pero NO se creó mask para fx="${fxId}" (anchor kind=${anchor.kind})`
      );
    }

    const id = this.nextId++;
    const active: ActiveEmitter = {
      id,
      fxId,
      def,
      anchor,
      ownerId: opts.ownerId,
      priority: opts.priority ?? 'normal',
      disableLOD: opts.disableLOD ?? false,
      startedAt: this.scene.time.now,
      backend,
      alive: true,
      bitmapMask,
      lastAnchorX: resolved.x,
      lastAnchorY: resolved.y,
    };
    this.emitters.set(id, active);

    // Projectile: las partículas mueren naturalmente al cumplirse su lifespan
    // (= flightMs), llegando al target via moveTo. El fade visual al llegar
    // se hace con un tween del `emitter.alpha` (afecta a todas las partículas
    // multiplicativamente) en el último 15% del vuelo, así el blob se mantiene
    // sólido hasta casi tocar el tile y se disuelve justo ahí.
    if (anchor.kind === 'projectile') {
      console.log(
        `[ParticleManager] PROJECTILE spawn fx="${fxId}" backend=${backend.engine} flightMs=${anchor.durationMs.toFixed(0)} moveTo=(${moveTo?.x.toFixed(0)},${moveTo?.y.toFixed(0)}) emitterPos=(${resolved.x.toFixed(0)},${resolved.y.toFixed(0)})`
      );
      if (backend.engine === 'v2') {
        const fadeStart = anchor.durationMs * 0.85;
        const fadeDuration = anchor.durationMs * 0.15;
        this.scene.tweens.add({
          targets: backend.emitter,
          alpha: 0,
          delay: fadeStart,
          duration: fadeDuration,
          ease: 'Power2',
        });
      }
      this.scene.time.delayedCall(anchor.durationMs + 100, () => {
        active.alive = false;
      });
    } else if (!def.loop && def.duration > 0) {
      // Auto-cleanup para FX one-shot
      this.scene.time.delayedCall(def.duration + def.emission.life[1] + 200, () => {
        active.alive = false;
      });
    }

    return { id };
  }

  /**
   * Devuelve una def clonada (shallow) con overrides para que el FX se
   * comporte como un burst único de partículas que viajan al target via
   * `moveTo` durante exactamente el flightMs. No muta la def original.
   *
   * Cambios respecto al catálogo:
   *   - loop=false, duration=0 → no afecta el ciclo (las partículas tienen vida fija).
   *   - emission.rate=0 → el adapter lo configura como burst (frequency=-1)
   *     y el caller dispara explode() después de crear el emitter.
   *   - emission.life=[flightMs,flightMs] → todas viven el viaje completo
   *     y mueren juntas al llegar al target.
   *   - alphaOverLife/sizeOverLife forzadas a constante: la heurística
   *     peak-aware del adapter aplana curvas multi-stop (1→0 lineal) y
   *     las partículas se desvanecen mucho antes de llegar al target. El
   *     fade visual lo hace un tween al `emitter.alpha` en el último 15%
   *     del vuelo (ver spawn rama projectile).
   */
  private overrideForProjectile(
    def: ParticleEffectDefV2,
    flightMs: number
  ): ParticleEffectDefV2 {
    const burstCount = def.emission.burstCount ?? def.emission.maxAlive;
    return {
      ...def,
      loop: false,
      duration: 0,
      emission: {
        ...def.emission,
        rate: 0,
        burstCount,
        maxAlive: burstCount,
        life: [flightMs, flightMs],
      },
      alphaOverLife: { stops: [[0, 1], [1, 1]] },
      sizeOverLife: { stops: [[0, 1], [1, 1]] },
    };
  }

  stop(handle: EmitterHandle): void {
    const active = this.emitters.get(handle.id);
    if (!active) return;
    active.alive = false;
    if (active.backend.engine === 'v2') active.backend.emitter.stop();
    else active.backend.emitter.stop();
  }

  stopAllByOwner(ownerId: string): void {
    for (const active of this.emitters.values()) {
      if (active.ownerId === ownerId) {
        active.alive = false;
        if (active.backend.engine === 'v2') active.backend.emitter.stop();
        else active.backend.emitter.stop();
      }
    }
  }

  setPriority(handle: EmitterHandle, priority: Priority): void {
    const active = this.emitters.get(handle.id);
    if (active) active.priority = priority;
  }

  /** Llamado desde GameScene.update(time, delta). */
  updateAll(delta: number): void {
    const cam = this.scene.cameras.main;
    const viewportLeft = cam.scrollX;
    const viewportTop = cam.scrollY;
    const viewportRight = viewportLeft + cam.width;
    const viewportBottom = viewportTop + cam.height;
    const margin = 64;

    for (const active of Array.from(this.emitters.values())) {
      // Cleanup de emitters marcados muertos
      if (!active.alive) {
        this.destroyActive(active);
        this.emitters.delete(active.id);
        continue;
      }

      const resolved = this.resolver.resolve(active.anchor);
      if (!resolved.valid) {
        active.alive = false;
        continue;
      }

      // Projectile (v2 nativo): el emitter queda fijo en el origen donde se
      // creó. Las partículas viajan al target por su cuenta via `moveTo`
      // configurado al spawn. Si moviéramos el emitter, la transformación
      // del managerMatrix arrastraría visualmente a las partículas también
      // → el blob avanzaría al doble de velocidad y saldría del path.
      const isProjectile = active.anchor.kind === 'projectile';

      // Entity drag: push-back en contra del desplazamiento del anchor,
      // emulando resistencia del aire / inercia. Si el char camina +X,
      // las partículas vivas se desplazan -X * drag (quedan más atrás
      // que el world space natural). Guard anti-teleport ignora saltos
      // grandes (cambio de zona, respawn, snap del server).
      const drag = active.def.entityDrag ?? 0;
      if (
        !isProjectile &&
        drag > 0 &&
        active.lastAnchorX !== undefined &&
        active.lastAnchorY !== undefined
      ) {
        const dx = resolved.x - active.lastAnchorX;
        const dy = resolved.y - active.lastAnchorY;
        const TELEPORT_THRESHOLD = 64;
        if ((dx !== 0 || dy !== 0) && Math.hypot(dx, dy) < TELEPORT_THRESHOLD) {
          shiftAliveParticles(active.backend, -dx * drag, -dy * drag);
        }
      }
      active.lastAnchorX = resolved.x;
      active.lastAnchorY = resolved.y;

      // Posicionar + update per backend.
      // Projectile v2 NO se reposiciona — el emitter queda fijo, las
      // partículas viajan por moveTo.
      if (active.backend.engine === 'v2') {
        if (!isProjectile) {
          active.backend.emitter.setPosition(resolved.x, resolved.y);
        }
        active.backend.emitter.setDepth(resolved.depth + (active.def.depthOffset ?? 0));
      } else {
        const e = active.backend.emitter;
        e.setPosition(resolved.x, resolved.y);
        // depthMode orbit: el split front/back (baseDepth ± 1) tiene que
        // straddlear el DEPTH REAL del char, no el depth resuelto del anchor.
        // El anchor 'feet' devuelve charDepth-1 y 'center' charDepth+100 (offsets
        // pensados para FX fijos), lo que rompía el split (front quedaba empatado
        // con el body o todo por encima). Para orbit usamos entity.sprite.depth
        // → front = charDepth+1 (sobre head/helm), back = charDepth-1 (detrás).
        let orbitBaseDepth = resolved.depth;
        if (active.def.depthMode === 'orbit' && active.anchor.kind === 'entity') {
          const ent = this.ctx.getEntity(active.anchor.entityId);
          if (ent) orbitBaseDepth = ent.sprite.depth;
        }
        e.setBaseDepth(orbitBaseDepth);
        // depth global del pool: behindEntity → -1, default → +50
        const behind = active.def.behindEntity ?? false;
        e.setDepth(behind ? orbitBaseDepth - 1 : orbitBaseDepth + 50);
        e.update(delta);
      }

      // Frustum culling — pausar emisión si fuera del viewport.
      // Excepción: projectiles nunca se cullean (son cortos y rápidos).
      const inViewport =
        resolved.x >= viewportLeft - margin &&
        resolved.x <= viewportRight + margin &&
        resolved.y >= viewportTop - margin &&
        resolved.y <= viewportBottom + margin;

      if (!isProjectile && !inViewport) {
        if (active.backend.engine === 'v2') active.backend.emitter.pause();
        continue;
      }
      if (active.backend.engine === 'v2') active.backend.emitter.resume();

      // LOD por distancia a la cámara (placeholder, no aplicado todavía).
      if (!active.disableLOD) {
        const camCenterX = viewportLeft + cam.width / 2;
        const camCenterY = viewportTop + cam.height / 2;
        const dx = resolved.x - camCenterX;
        const dy = resolved.y - camCenterY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const lodMultiplier = dist < LOD_NEAR ? 1 : dist < LOD_FAR ? 0.5 : 0.25;
        void lodMultiplier;
      }
    }
  }

  /** Métricas para el HUD del editor. */
  getMetrics(): { activeEmitters: number; budget: number } {
    return {
      activeEmitters: this.emitters.size,
      budget: BUDGET_MAX_EMITTERS,
    };
  }

  destroy(): void {
    for (const active of this.emitters.values()) {
      this.destroyActive(active);
    }
    this.emitters.clear();
  }

  // ── Cleanup helper ────────────────────────────────────────────────────────

  private destroyActive(active: ActiveEmitter): void {
    if (active.backend.engine === 'v2') active.backend.emitter.destroy();
    else active.backend.emitter.destroy();
    // BitmapMask no se auto-destruye; liberarla acá evita leak de la
    // render texture interna que crea Phaser.
    active.bitmapMask?.destroy();
  }

  // ── Internos ────────────────────────────────────────────────────────────

  /**
   * Si la def pide clip al sprite del anchor y el anchor es de tipo entity
   * con un slot de equipo (weapon_tip/weapon_hilt/shield_front/helm_top),
   * resuelve el sprite fuente y devuelve una BitmapMask. La máscara sigue
   * automáticamente al sprite (matriz + frame), así que no requiere update
   * cuando el char se mueve o cambia de dirección.
   *
   * Devuelve undefined si: (a) la def no pide clip, (b) el anchor no es
   * compatible, (c) la entidad o el sprite del slot no existen.
   */
  private tryCreateAnchorClipMask(
    def: ParticleEffectDefV2,
    anchor: Anchor
  ): Phaser.Display.Masks.BitmapMask | undefined {
    if (!def.clipToAnchorSprite) return undefined;
    if (anchor.kind !== 'entity') return undefined;

    const entity = this.ctx.getEntity(anchor.entityId);
    if (!entity) return undefined;

    const source = pickAnchorSpriteForSlot(entity, anchor.slot);
    if (!source) {
      console.warn(
        `[ParticleManager] clipToAnchorSprite: slot "${anchor.slot}" sin sprite en ${anchor.entityId} (fx="${def.id}")`
      );
      return undefined;
    }

    return source.createBitmapMask();
  }

  /**
   * Instancia un emitter v1 (sprite-based) con la def convertida v2→v1.
   * Soporta orbit, depthMode='orbit', y todo lo que el motor v1 ya hace.
   */
  private createV1Backend(
    def: ParticleEffectDefV2,
    x: number,
    y: number,
    initialDepth: number
  ): ActiveEmitterV1 {
    const v1 = v2ToV1(def);
    if (!v1.spriteKey || !this.scene.textures.exists(v1.spriteKey)) {
      v1.spriteKey = PARTICLE_SHEET.key;
    }
    const emitter = new ParticleEmitter(this.scene, v1, x, y);
    if (v1.depthMode === 'orbit') {
      // baseDepth se actualiza cada frame en updateAll().
      emitter.setBaseDepth(initialDepth);
      emitter.setDepth(initialDepth);
    } else {
      const behind = v1.behindEntity ?? false;
      emitter.setDepth(behind ? initialDepth - 1 : initialDepth + 50);
    }
    emitter.start();
    return { engine: 'v1', emitter };
  }

  private enforceBudget(incomingPriority: Priority): void {
    if (this.emitters.size < BUDGET_MAX_EMITTERS) return;

    const priorityOrder: Record<Priority, number> = { low: 0, normal: 1, high: 2 };
    const incoming = priorityOrder[incomingPriority];

    let victim: ActiveEmitter | null = null;
    for (const active of this.emitters.values()) {
      const candidate = priorityOrder[active.priority];
      if (candidate >= incoming) continue;
      if (!victim || active.startedAt < victim.startedAt) {
        victim = active;
      }
    }
    if (victim) {
      victim.alive = false;
    }
  }
}
